class DdLoopCountdown extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.hoursEl = this.querySelector('[data-dd-hours]');
    this.minutesEl = this.querySelector('[data-dd-minutes]');
    this.secondsEl = this.querySelector('[data-dd-seconds]');
    this.progressEl = this.querySelector('[data-dd-progress]');
    this.hideHours = this.dataset.hideHours === 'true';
    this.storageKey = this.dataset.storageKey || `dd-loop-countdown-${this.id || Math.random()}`;
    this.durationMs = Math.max(
      1000,
      (Number(this.dataset.hours) || 0) * 3600000 +
        (Number(this.dataset.minutes) || 0) * 60000 +
        (Number(this.dataset.seconds) || 0) * 1000
    );

    this.endAt = this.#resolveEndAt();
    this.#tick();
    this.timer = window.setInterval(() => this.#tick(), 250);
  }

  disconnectedCallback() {
    if (this.timer) window.clearInterval(this.timer);
  }

  #resolveEndAt() {
    const loop = this.dataset.loop !== 'false';
    let endAt = Number(localStorage.getItem(this.storageKey) || 0);
    const now = Date.now();

    if (!endAt || endAt <= now) {
      endAt = now + this.durationMs;
      if (loop) localStorage.setItem(this.storageKey, String(endAt));
    }
    return endAt;
  }

  #renew() {
    this.endAt = Date.now() + this.durationMs;
    if (this.dataset.loop !== 'false') {
      localStorage.setItem(this.storageKey, String(this.endAt));
    }
  }

  #setValue(el, value) {
    if (!el) return;
    const next = String(value).padStart(2, '0');
    if (el.textContent === next) return;
    el.textContent = next;
    const unit = el.closest('.dd-loop-countdown__unit');
    if (!unit) return;
    unit.classList.remove('dd-loop-countdown__unit--tick');
    // Force reflow so animation can replay
    void unit.offsetWidth;
    unit.classList.add('dd-loop-countdown__unit--tick');
  }

  #setProgress(distance) {
    if (!this.progressEl) return;
    const ratio = Math.max(0, Math.min(1, distance / this.durationMs));
    this.progressEl.style.transform = `scaleX(${ratio})`;
  }

  #tick() {
    let distance = this.endAt - Date.now();
    if (distance <= 0) {
      if (this.dataset.loop === 'false') {
        distance = 0;
      } else {
        this.#renew();
        distance = this.endAt - Date.now();
      }
    }

    const totalSeconds = Math.floor(distance / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (!this.hideHours) this.#setValue(this.hoursEl, hours);
    this.#setValue(this.minutesEl, this.hideHours ? Math.floor(totalSeconds / 60) : minutes);
    this.#setValue(this.secondsEl, seconds);
    this.#setProgress(distance);
  }
}

customElements.define('dd-loop-countdown', DdLoopCountdown);

class DdPriceOff extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.mode = this.dataset.mode || 'sale';
    this.offset = Number(this.dataset.offsetCents) || 0;
    this.showProductCompare = this.dataset.showProductCompare === 'true';
    this.showSavings = this.dataset.showSavings === 'true';
    this.showOrigSavings = this.dataset.showOrigSavings === 'true';
    this.showCartSalePrice = this.dataset.showCartSalePrice === 'true';
    this.currencyEnabled = this.dataset.currencyCode === 'true';
    this.saleEl = this.querySelector('[data-dd-sale-price]');
    this.compareEl = this.querySelector('[data-dd-compare-price]');
    this.productCompareEl = this.querySelector('[data-dd-product-compare]');
    this.productCompareAmountEl = this.querySelector('[data-dd-product-compare-amount]');
    this.savingsInput = this.querySelector('[data-dd-savings-input]');
    this.savingsLabelInput = this.querySelector('[data-dd-savings-label-input]');
    this.origSavingsInput = this.querySelector('[data-dd-orig-savings-input]');
    this.origSavingsLabelInput = this.querySelector('[data-dd-orig-savings-label-input]');
    this.saleUnitInput = this.querySelector('[data-dd-sale-unit-input]');

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.dataset.section;
        if (!sectionId || event.data.sectionId !== sectionId) return;
        const variant = event.data.variant;
        if (!variant) return;
        this.#render(variant.price, variant.compare_at_price);
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  #formatMoney(cents) {
    const amount = Math.max(0, Number(cents) || 0);
    const format =
      this.dataset.moneyFormat ||
      window.theme?.moneyFormat ||
      (typeof Shopify !== 'undefined' ? Shopify.money_format : null);

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && format) {
      return Shopify.formatMoney(amount, format);
    }

    const value = (amount / 100).toFixed(2);
    if (format) {
      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(amount / 100)))
        .replace(/\{\{\s*amount\s*\}\}/g, value);
    }

    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'PKR',
      }).format(amount / 100);
    } catch (error) {
      return value;
    }
  }

  #syncSavingsProperty(saleCents, compareAtCents) {
    if (!this.showSavings || !this.savingsInput) return;

    const savings = Math.max(0, (Number(compareAtCents) || 0) - (Number(saleCents) || 0));
    const enabled = savings > 0 && (Number(compareAtCents) || 0) > (Number(saleCents) || 0);

    this.savingsInput.value = String(savings);
    this.savingsInput.disabled = !enabled;
    if (this.savingsLabelInput) this.savingsLabelInput.disabled = !enabled;
  }

  #syncOrigSavingsProperty(originalCents, compareAtCents) {
    if (!this.showOrigSavings || !this.origSavingsInput) return;

    const savings = Math.max(0, (Number(compareAtCents) || 0) - (Number(originalCents) || 0));
    const enabled = savings > 0 && (Number(compareAtCents) || 0) > (Number(originalCents) || 0);

    this.origSavingsInput.value = String(savings);
    this.origSavingsInput.disabled = !enabled;
    if (this.origSavingsLabelInput) this.origSavingsLabelInput.disabled = !enabled;
  }

  #syncSaleUnitProperty(saleCents) {
    if (!this.showCartSalePrice || !this.saleUnitInput) return;
    this.saleUnitInput.value = String(Math.max(0, Number(saleCents) || 0));
    this.saleUnitInput.disabled = false;
  }

  #render(priceCents, compareAtCents) {
    const original = Number(priceCents) || 0;
    const sale = Math.max(0, original - this.offset);
    const productCompare = Number(compareAtCents) || 0;

    if (this.mode === 'sale' && this.saleEl) {
      this.saleEl.textContent = this.#formatMoney(sale);
    }
    if (this.mode === 'compare' && this.compareEl) {
      this.compareEl.textContent = this.#formatMoney(original);
    }
    if (this.mode === 'both') {
      if (this.saleEl) this.saleEl.textContent = this.#formatMoney(sale);
      if (this.compareEl) this.compareEl.textContent = this.#formatMoney(original);
    }

    if (this.showProductCompare && this.productCompareEl) {
      const showWas = productCompare > original;
      this.productCompareEl.classList.toggle('dd-price-off__was--hidden', !showWas);
      if (showWas && this.productCompareAmountEl) {
        this.productCompareAmountEl.textContent = this.#formatMoney(productCompare);
      }
    }

    this.#syncSavingsProperty(sale, productCompare);
    this.#syncOrigSavingsProperty(original, productCompare);
    this.#syncSaleUnitProperty(sale);
  }
}

customElements.define('dd-price-off', DdPriceOff);
